/*
 ** Button elements (Drawer triggers)
 */
class ProductOptionsButton extends window.BAO.CustomElement() {
  static get styles () {
    return `
      :host {
        display: block;
      }
    `
  }

  static get requiredAttributes () {
    return ['product-handle']
  }

  constructor () {
    super()

    if (this.drawer == null) {
      this.throwError(
        `requires <site-drawer key="${this.drawerKey}"> element to exist on the page`,
      )
    }

    if (this.productOptionsEl == null) {
      this.throwError(
        `requires <${this.localName.replace(
          '-button',
          '',
        )}> element to exist on the page`,
      )
    }
  }

  connectedCallback () {
    super.connectedCallback()
  }

  setupListeners () {
    super.setupListeners()

    if (!this.productOptionsEl) return

    this.listeners.add(this, 'click', this.handleClick.bind(this))
    this.listeners.add(this, 'focusin', this.handleFocus.bind(this))
    this.listeners.add(this, 'mouseenter', this.handleMouseEnter.bind(this))
  }

  handleClick (event) {
    this.triggerOptionRender()

    this.buttonEl = event.target.closest('button')

    if (!this.buttonEl) {
      event.preventDefault()
      return
    }

    this.drawers.open(this.drawer.getAttribute('key'), this.buttonEl)
  }

  handleFocus () {
    this.triggerOptionRender()
  }

  handleMouseEnter () {
    this.triggerOptionRender()
  }

  triggerOptionRender () {
    this.productOptionsEl.setAttribute('quickbuy', this.isQuickBuy)
    this.productOptionsEl.setAttribute('product-handle', this.productHandle)
  }

  get buttonEl () {
    return this._buttonEl
  }

  set buttonEl (buttonEl) {
    const disabled =
      !buttonEl ||
      buttonEl?.disabled ||
      buttonEl?.getAttribute('aria-disabled') === 'true'
    this._buttonEl = disabled ? null : buttonEl
  }

  get productHandle () {
    return this.getAttribute('product-handle')
  }

  get isQuickBuy () {
    return this.getAttribute('quickbuy') === 'true'
  }

  get drawers () {
    return window.BAO.utils.getElement('site-drawers')
  }

  get drawer () {
    return window.BAO.utils.getElement(`site-drawer[key=${this.drawerKey}]`)
  }

  get drawerKey () {}

  get productOptionsEl () {}
}
if (!customElements.get('product-options-button')) {
  customElements.define('product-options-button', ProductOptionsButton)
}

class ProductOptionsColourButton extends ProductOptionsButton {
  get drawerKey () {
    return 'product-options-colour'
  }

  get productOptionsEl () {
    return window.BAO.utils.getElement('product-options-colour')
  }
}
if (!customElements.get('product-options-colour-button')) {
  customElements.define(
    'product-options-colour-button',
    ProductOptionsColourButton,
  )
}

class ProductOptionsVariantButton extends ProductOptionsButton {
  static get requiredAttributes () {
    return ['product-handle', 'form-id']
  }

  get drawerKey () {
    return 'product-options-variant'
  }

  get productOptionsEl () {
    return window.BAO.utils.getElement('product-options-variant')
  }

  get productFormId () {
    return this.getAttribute('form-id')
  }

  triggerOptionRender () {
    super.triggerOptionRender()

    this.productOptionsEl.setAttribute('form-id', this.productFormId)
  }
}
if (!customElements.get('product-options-variant-button')) {
  customElements.define(
    'product-options-variant-button',
    ProductOptionsVariantButton,
  )
}

/*
 ** Option elements (Drawer content renderer)
 */
class ProductOptions extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['body']
  }

  static get observedAttributes () {
    return ['product-handle']
  }

  constructor () {
    super()
    this.cache = new Map()
  }

  attributeChangedCallback (name, oldValue, newValue) {
    if (name === 'product-handle') {
      this.onProductHandleChange(oldValue, newValue)
    }
  }

  async onProductHandleChange (previousProductHandle, newProductHandle) {
    const variantData = {
      key: this.variantId?.length ? `${this.variantId}-` : '',
      query: this.variantId?.length ? `?variant=${this.variantId}` : '',
    }

    this.currentCacheKey = `${newProductHandle}-${variantData.key}${this.sectionId}`

    if (
      !previousProductHandle?.length ||
      (this.previousCacheKey === this.currentCacheKey && this.hasContent)
    ) {
      return
    }

    const cachedHtml = this.cache.has(this.currentCacheKey)
      ? this.cache.get(this.currentCacheKey)
      : null

    this.html =
      cachedHtml ||
      (await window.BAO.sectionRenderer.fetchSingle(
        this.sectionId,
        `/products/${newProductHandle}${variantData.query}`,
      ))

    this.cache.set(this.currentCacheKey, this.html)

    this.beforeRender()

    this.sectionHTML = window.BAO.sectionRenderer.getInnerHtml(this.html)

    this.afterRender()
    this.previousCacheKey = this.currentCacheKey
  }

  beforeRender () {}

  afterRender () {}

  set sectionHTML (html) {
    this.els.body.element.innerHTML = html
    this.hasContent = true
  }

  get sectionHTML () {
    return this.els.body.element.innerHTML
  }

  get productHandle () {
    return this.getAttribute('product-handle')
  }

  get variantId () {
    return this.getAttribute('variant-id')
  }

  get sectionId () {}
}
if (!customElements.get('product-options')) {
  customElements.define('product-options', ProductOptions)
}

class ProductOptionsColour extends ProductOptions {
  get sectionId () {
    return 'push-product-options-colour'
  }
}
if (!customElements.get('product-options-colour')) {
  customElements.define('product-options-colour', ProductOptionsColour)
}

class ProductOptionsVariant extends ProductOptions {
  static get observedAttributes () {
    return ['product-handle', 'form-id']
  }

  setupListeners () {
    super.setupListeners()
    this.listeners.add(this, 'click', this.handleClick.bind(this))
  }

  attributeChangedCallback (name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue)

    if (oldValue === newValue) return

    if (name === 'form-id') {
      this.productFormId = newValue
    }
  }

  beforeRender () {
    super.beforeRender()

    const dom = window.BAO.sectionRenderer.convertToDOM(this.html)
    this.updateFormIdAttribute(dom)
    this.updateTitleValue(dom)

    this.html = dom.body.innerHTML
  }

  updateFormIdAttribute (context) {
    const formAttributeEls = window.BAO.utils.getElements('[form]', { context })
    if (!formAttributeEls) return
    formAttributeEls.forEach(formAttributeEl => {
      formAttributeEl.setAttribute('form', this.productFormId)
    })
  }

  updateTitleValue (context) {
    const titleEl = window.BAO.utils.getElement(this.titleSelector, {
      context: this,
    })
    const titleValueEl = window.BAO.utils.getElement(this.titleValueSelector, {
      context,
    })

    if (!titleEl || !titleValueEl) return
    titleEl.textContent = titleValueEl.textContent
  }

  handleClick (event) {
    if (
      event?.target?.closest('button')?.name === 'add' ||
      event.target.closest('a')
    ) {
      return
    }
    event.preventDefault()

    const buttonEl = event.target.closest(this.buttonSelector)
    if (!buttonEl) return

    this.buttonEls.forEach(buttonEl => {
      buttonEl.setAttribute(
        'aria-current',
        String(buttonEl.value === buttonEl?.value),
      )
    })

    window.BAO.dispatchEvent(window.BAO.EVENTS.PRODUCT.VARIANT_SELECTED, {
      formId: this.productFormId,
      variant: window.BAO.utils.objectFromJsonString(buttonEl.dataset.variant),
    })
  }

  get productFormId () {
    return this._productFormId
  }

  set productFormId (formId) {
    this._productFormId = formId

    this.updateFormIdAttribute(this)
  }

  get titleSelector () {
    return `[data-${this.localName}-el="title"]`
  }

  get titleValueSelector () {
    return `[data-${this.localName}-el="title-value"]`
  }

  get buttonSelector () {
    return `[data-${this.localName}-el="button"]`
  }

  get buttonEls () {
    return window.BAO.utils.getElements(this.buttonSelector, { context: this })
  }

  get sectionId () {
    return this.getAttribute('quickbuy') === 'true'
      ? 'push-product-options-variant-quickbuy'
      : 'push-product-options-variant'
  }
}
if (!customElements.get('product-options-variant')) {
  customElements.define('product-options-variant', ProductOptionsVariant)
}
